// Flow: tesenergy.hr -> /sso/start -> auth.tesenergy.hr -> tesenergy.hr/sso-resume -> /sso/mint -> portal.tesenergy.hr/sso

export const API = "https://api.tesenergy.hr";
export const AUTH_UI = "https://auth.tesenergy.hr";
export const DEFAULT_RETURN_TO = "/dolibarr/";
export const RESUME_PATH = "/sso-resume";
export const RESUME_URL = `${window.location.origin}${RESUME_PATH}`;

// Auth UI is served at root.
export const AUTH_PATH = "/";

const STATE_KEY = "portal_sso_state";
const RETURN_TO_KEY = "portal_sso_returnTo";
const RETRY_KEY = "portal_sso_resume_retry";

function sanitizeReturnTo(returnTo?: string) {
  if (typeof returnTo !== "string") return DEFAULT_RETURN_TO;
  if (!returnTo.startsWith("/")) return DEFAULT_RETURN_TO;
  if (returnTo.startsWith("//")) return DEFAULT_RETURN_TO;
  return returnTo;
}

function authRedirectUrl() {
  return `${AUTH_UI}${AUTH_PATH}?redirectTo=${encodeURIComponent(RESUME_URL)}`;
}

export function clearPortalSSOState() {
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETRY_KEY);
}

function notifyUser(message: string) {
  if (typeof window !== "undefined" && window.alert) {
    window.alert(message);
  }
}

export async function startPortalSSO(returnTo?: string): Promise<void> {
  const safeReturnTo = sanitizeReturnTo(returnTo);
  clearPortalSSOState();

  try {
    const response = await fetch(`${API}/sso/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ returnTo: safeReturnTo }),
    });

    // If CORS fails, backend must allow:
    // Access-Control-Allow-Origin: https://tesenergy.hr
    // Access-Control-Allow-Credentials: true
    if (!response.ok) {
      const text = await response.text();
      console.error("SSO start failed", response.status, text);
      clearPortalSSOState();
      notifyUser("Login is currently unavailable. Please try again.");
      return;
    }

    const data = (await response.json()) as { state?: string };
    if (!data.state) {
      console.error("SSO start missing state", data);
      clearPortalSSOState();
      notifyUser("Login is currently unavailable. Please try again.");
      return;
    }

    sessionStorage.setItem(STATE_KEY, data.state);
    sessionStorage.setItem(RETURN_TO_KEY, safeReturnTo);

    window.location.assign(authRedirectUrl());
  } catch (error) {
    console.error("SSO start error", error);
    clearPortalSSOState();
    notifyUser("Login is currently unavailable. Please try again.");
  }
}

export async function resumePortalSSOIfNeeded(): Promise<void> {
  const state = sessionStorage.getItem(STATE_KEY);
  if (!state) return;

  try {
    const response = await fetch(`${API}/sso/mint`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ state }),
    });

    if (response.status === 401 || response.status === 403) {
      const hasRetried = sessionStorage.getItem(RETRY_KEY) === "1";
      if (hasRetried) {
        clearPortalSSOState();
        notifyUser("Your login session expired. Please start sign-in again.");
        throw new Error("SSO mint unauthorized after retry");
      }
      sessionStorage.setItem(RETRY_KEY, "1");
      window.location.assign(authRedirectUrl());
      return;
    }

    if (!response.ok) {
      const text = await response.text();
      console.error("SSO mint failed", response.status, text);
      clearPortalSSOState();
      throw new Error("SSO mint failed");
    }

    const data = (await response.json()) as { redirectTo?: string };
    if (!data.redirectTo) {
      console.error("SSO mint missing redirectTo", data);
      clearPortalSSOState();
      throw new Error("Missing redirectTo");
    }

    clearPortalSSOState();

    window.location.assign(data.redirectTo);
  } catch (error) {
    console.error("SSO resume error", error);
    notifyUser("We couldn't sign you in. Please try again.");
    throw error;
  }
}
